import {
  NotificationBulkPrintResource,
  NotificationResource,
} from '@appTypes/NotificationResource';
import { BulkPrintRequestResource } from '@appTypes/PortfolioBulkActionResource';
import { DealsheetService } from '@services/dealsheet/DealsheetService';
import { DismissAllToasts, ErrorToast } from '@services/utils/ToastUtils';
import { AppState } from '@store/AppState';
import { setBulkPrintShouldLoadAction } from '@store/notifications/NotificationActions';
import { INotificationsState } from '@store/notifications/NotificationReducer';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { uniq } from 'underscore';
import { useRouterService } from './common/UseRouterService';

export const useNotificationBulkPrint = (): NotificationBulkPrintType => {
  const maxNoOfDealsheetsToExport: number = 25;
  const [isBulkPrintVisible, setIsBulkPrintVisible] = useState<boolean>(false);
  const [bulkPrintNotifications, setBulkPrintNotifications] = useState<
    NotificationBulkPrintResource[]
  >([]);
  const { filteredItems, filter } = useSelector<AppState, INotificationsState>(
    (notificationState) => notificationState.notification,
  );
  const dispatch = useDispatch();
  const { goToBulkPrint } = useRouterService();

  const findUniqueDealsheetsToExport = (filteredItems: NotificationResource[]) => {
    const filteredUnreadItemsForExport = filteredItems
      ? filteredItems.filter(
          (n) =>
            // notification is unread
            !n.isRead &&
            // notification is not Service-To-Sales and if it is Service-To-Sales, it should now be part of Loyalty
            (!n.isConquest || (n.isConquest && !(n.dealType === 'Unknown'))) &&
            // notification is not Market Conquest
            !(
              n.notificationType === 'AssignedMarketConquestDeal' ||
              (n.notificationType === 'AssignedDeal' && n.notificationValue === 'MarketConquest')
            ) &&
            !(
              n.notificationType === 'ConquestCampaignClick' ||
              (n.notificationType === 'EmailCampaignClick' &&
                n.notificationValue === 'MarketConquest')
            ) &&
            // notification dealtype is not unknown
            !(n.dealType === 'Unknown'),
        )
      : null;

    let vinsToExport = filteredUnreadItemsForExport
      ? filteredUnreadItemsForExport.map((n) => n.vin)
      : null;
    vinsToExport =
      vinsToExport && vinsToExport.length > 0 ? Array.from(new Set(vinsToExport)) : null;
    vinsToExport = vinsToExport ? vinsToExport.slice(0, maxNoOfDealsheetsToExport) : null;

    let dealsToExport: NotificationBulkPrintResource[] = filteredUnreadItemsForExport
      ? filteredUnreadItemsForExport
          .filter((item) => vinsToExport?.indexOf(item.vin) !== -1)
          .map(({ vin, dealerId, name }) => ({
            vin,
            dealerId,
            customerName: name,
          }))
      : [];

    dealsToExport = uniq(dealsToExport, (deal) => [deal.vin, deal.dealerId].join());

    return dealsToExport;
  };

  const getNotifTooltipFromFilter = (notifFilter: string): string => {
    switch (notifFilter) {
      case 'ServiceAppointment':
        return 'Service Appointments';

      case 'AutoActivatedBySMm':
        return 'Service-to-Sales';

      case 'FinalPayment':
        return 'Payment Due';

      case 'Birthday':
        return 'Birthdays';

      case 'Anniversary':
        return 'Vehicle Anniversary';

      case 'MdlServiceArrival':
        return 'Service Arrivals';

      case 'EmailCampaignClick':
        return 'Campaign Activity';

      case 'AssignedDeal':
        return 'Deal Assignments';

      case 'PrivateIncentive':
        return 'Private Incentives';

      default:
        return '';
    }
  };

  const bulkPrintOffer = (): void => {
    dispatch(setBulkPrintShouldLoadAction(true));
    const payload: BulkPrintRequestResource = {
      deals: bulkPrintNotifications,
      notifCategory: getNotifTooltipFromFilter(filter),
      portfolioRequestResource: null,
    };
    DealsheetService.getBulkPrintOfferData(payload)
      .then((response) => {
        const bulkPrintOfferResponse = {
          printData: response,
        };
        localStorage.setItem('portfolioBulkPrint', JSON.stringify(bulkPrintOfferResponse));
        const successRecordsCount = response?.filter(
          (printData) => printData.isDataLoaded === true,
        )?.length;

        if (successRecordsCount && successRecordsCount > 0) {
          goToBulkPrint().then((isPopupBlocked: boolean) => {
            DismissAllToasts();
            if (isPopupBlocked) {
              localStorage.setItem('portfolioBulkPrint', '');
              ErrorToast(
                'Pop-ups are currently disabled in your browser. Please select “Always Allow” and download the deal sheets again to enable the new tab to open.',
                false,
              );
            }
            dispatch(setBulkPrintShouldLoadAction(false));
          });
        } else {
          DismissAllToasts();
          localStorage.setItem('portfolioBulkPrint', '');
          dispatch(setBulkPrintShouldLoadAction(false));
          ErrorToast(
            'There was an error in producing the deal sheets you requested. Please try again.',
            false,
          );
        }
      })
      .catch((err) => {
        DismissAllToasts();
        localStorage.setItem('portfolioBulkPrint', '');
        ErrorToast(
          'There was an error in producing the deal sheets you requested. Please try again.',
          false,
        );
        dispatch(setBulkPrintShouldLoadAction(false));
      });
  };

  useEffect(() => {
    const dealsToExport = findUniqueDealsheetsToExport(filteredItems);
    if (dealsToExport && dealsToExport.length > 0) {
      setIsBulkPrintVisible(true);
      setBulkPrintNotifications(dealsToExport);
    } else {
      setIsBulkPrintVisible(false);
    }
  }, [filteredItems]);

  return {
    isBulkPrintVisible,
    bulkPrintOffer,
    getNotifTooltipFromFilter,
    bulkPrintNotifications,
  };
};

type NotificationBulkPrintType = {
  isBulkPrintVisible: boolean;
  bulkPrintOffer: () => void;
  getNotifTooltipFromFilter: (filter: string) => string;
  bulkPrintNotifications: NotificationBulkPrintResource[];
};
